import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, Tag, Hash, Sparkles } from "lucide-react";
import { apiRequest } from "../../config/api";
import { debounce, getImageUrl } from "../../utils/helpers";
import CircularLoader from "../common/CircularLoader";

// Mobile-optimized CSS animations
const mobileSearchStyles = `
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes pulseGlow {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(236, 72, 153, 0.4);
    }
    50% {
      box-shadow: 0 0 0 10px rgba(236, 72, 153, 0);
    }
  }
  
  .mobile-search-enter {
    animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .mobile-input-focus {
    animation: fadeInScale 0.3s ease-out;
  }
  
  .mobile-search-glow {
    animation: pulseGlow 2s infinite;
  }
  
  .mobile-suggestion-hover {
    transform: translateX(8px);
    transition: transform 0.2s ease-out;
  }
`;

const MobileSearchBar = ({
  isOpen,
  onClose,
  value,
  onChange,
  placeholder = "Search for yarn, needles, patterns...",
  onSuggestionSelect,
  onSearchSubmit,
}) => {
  const [localValue, setLocalValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Focus input when modal opens and prevent body scroll
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchTerm) => {
        if (!searchTerm.trim() || searchTerm.length < 1) {
          setSuggestions([]);
          return;
        }

        try {
          setLoading(true);
          setError(null);

          const response = await apiRequest(
            `/products/search-suggestions?q=${encodeURIComponent(
              searchTerm
            )}&limit=8`
          );

          if (response.success) {
            setSuggestions(response.data);
          } else {
            setSuggestions([]);
          }
        } catch (err) {
          console.error("Search suggestions error:", err);
          setError("Failed to load suggestions");
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      onChange(newValue);
      debouncedSearch(newValue);
      setSelectedIndex(-1);
    },
    [onChange, debouncedSearch]
  );

  const handleClear = useCallback(() => {
    setLocalValue("");
    onChange("");
    setSuggestions([]);
    setSelectedIndex(-1);
    setError(null);
  }, [onChange]);

  const handleSuggestionClick = useCallback(
    (suggestion) => {
      if (onSuggestionSelect) {
        onSuggestionSelect(suggestion);
      } else {
        navigate(`/products/${suggestion.slug}`);
      }

      setLocalValue(suggestion.name);
      onChange(suggestion.name);
      onClose();
      setSelectedIndex(-1);
    },
    [onSuggestionSelect, navigate, onChange, onClose]
  );

  const handleSearch = useCallback(() => {
    if (localValue.trim()) {
      if (onSearchSubmit) {
        onSearchSubmit(localValue);
      } else {
        navigate(`/search?q=${encodeURIComponent(localValue.trim())}`);
      }
      onClose();
    }
  }, [localValue, onSearchSubmit, navigate, onClose]);

  const handleKeyDown = useCallback(
    (e) => {
      if (suggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            handleSuggestionClick(suggestions[selectedIndex]);
          } else {
            handleSearch();
          }
          break;
        case "Escape":
          onClose();
          break;
      }
    },
    [suggestions, selectedIndex, handleSuggestionClick, handleSearch, onClose]
  );


  const getMatchTypeIcon = useCallback((matchType) => {
    switch (matchType) {
      case "name":
        return <Search className="h-3 w-3" />;
      case "brand":
        return <Hash className="h-3 w-3" />;
      case "category":
      case "tag":
        return <Tag className="h-3 w-3" />;
      default:
        return <Search className="h-3 w-3" />;
    }
  }, []);

  const getMatchTypeColor = useCallback((matchType) => {
    switch (matchType) {
      case "name":
        return "text-primary-600 bg-primary-50 border-primary-200";
      case "brand":
        return "text-purple-600 bg-purple-50 border-purple-200";
      case "category":
        return "text-green-600 bg-green-50 border-green-200";
      case "tag":
        return "text-orange-600 bg-orange-50 border-orange-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <style>{mobileSearchStyles}</style>

      {/* Mobile Search Overlay */}
      <div className="fixed inset-0 z-50 lg:hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Search Container */}
        <div className="absolute inset-0 bg-white mobile-search-enter flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-accent-50">
            <h2 className="text-xl font-bold text-secondary-900 font-jakarta">
              Search Products
            </h2>
            <button
              onClick={onClose}
              className="p-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
              aria-label="Close search"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Search Input Section */}
          <div className="p-4">
            <div className="relative group">
              {/* Enhanced Search Input */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <div
                    className={`p-2 rounded-xl transition-all duration-300 ${
                      isFocused
                        ? "bg-primary-100 text-primary-600 shadow-lg"
                        : "text-gray-400"
                    }`}
                  >
                    <Search className="h-6 w-6" />
                  </div>
                </div>

                <input
                  ref={searchInputRef}
                  type="text"
                  value={localValue}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder={placeholder}
                  className={`block w-full pl-16 pr-20 py-4 border-2 rounded-2xl bg-white transition-all duration-300 text-lg font-medium font-jakarta placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-primary-500 ${
                    isFocused
                      ? "border-primary-500 shadow-xl"
                      : "border-gray-200"
                  }`}
                  autoComplete="off"
                />

                {/* Action Buttons */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-2">
                  {/* Search Button */}
                  {localValue.trim() && (
                    <button
                      onClick={handleSearch}
                      className="p-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95"
                      title="Search"
                    >
                      <Search className="h-5 w-5" />
                    </button>
                  )}

                  {/* Clear Button */}
                  {localValue && (
                    <button
                      onClick={handleClear}
                      className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-300"
                      title="Clear search"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Focus Indicator */}
              {isFocused && (
                <div className="absolute inset-0 rounded-2xl ring-2 ring-primary-200 ring-offset-2 pointer-events-none animate-pulse"></div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium font-jakarta transition-all duration-300">
                Popular
              </button>
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium font-jakarta transition-all duration-300">
                Recent
              </button>
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium font-jakarta transition-all duration-300">
                Categories
              </button>
            </div>
          </div>

          {/* Search Results */}
          <div
            ref={suggestionsRef}
            className="flex-1 overflow-y-auto px-4 pb-4 min-h-0"
            style={{ 
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
              maxHeight: "calc(100vh - 280px)"
            }}
          >
            {loading && (
              <div className="py-8 text-center">
                <CircularLoader
                  size="lg"
                  variant="primary"
                  text="Searching..."
                  showText={false}
                />
                <p className="text-lg font-semibold text-gray-700 font-jakarta">
                  Searching...
                </p>
                <p className="text-sm text-gray-500 mt-1 font-jakarta">
                  Finding perfect matches
                </p>
              </div>
            )}

            {error && (
              <div className="py-8 text-center">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-red-200">
                  <X className="w-10 h-10 text-red-500" />
                </div>
                <p className="text-lg font-semibold text-red-600 font-jakarta">
                  {error}
                </p>
                <p className="text-sm text-gray-500 mt-1 font-jakarta">
                  Please try again
                </p>
              </div>
            )}

            {!loading && !error && suggestions.length > 0 && (
              <div className="space-y-3 bg-white">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.id}
                    className={`p-4 rounded-2xl cursor-pointer transition-all duration-300 border-l-4 ${
                      index === selectedIndex
                        ? "bg-primary-50/80 border-l-primary-500 shadow-lg transform scale-[1.02]"
                        : "bg-white border-l-transparent hover:bg-gray-50/80 hover:border-l-primary-300 hover:shadow-md hover:mobile-suggestion-hover"
                    }`}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        {suggestion.image ? (
                          <img
                            src={getImageUrl(suggestion.image)}
                            alt={suggestion.name}
                            className="w-14 h-14 rounded-lg object-cover bg-gray-100 border"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src =
                                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' fill='%23f3f4f6'%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Inter, Arial, sans-serif' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                            }}
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-gray-100 border flex items-center justify-center">
                            <span className="text-xs text-gray-500">
                              No Image
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="text-base font-bold text-gray-900 font-jakarta leading-tight">
                              {suggestion.name}
                            </h4>
                            {suggestion.brand && (
                              <p className="text-sm text-gray-600 mt-1 font-medium font-jakarta">
                                by {suggestion.brand}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-3">
                            {typeof suggestion.price === "number" && (
                              <div className="text-right">
                                {suggestion.has_discount &&
                                suggestion.original_price ? (
                                  <div>
                                    <p className="text-sm text-gray-500 line-through font-jakarta">
                                      MRP ₹
                                      {suggestion.original_price.toFixed(2)}
                                    </p>
                                    <p className="text-lg font-bold text-primary-600 font-jakarta">
                                      ₹{suggestion.price.toFixed(2)}
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-lg font-bold text-primary-600 font-jakarta">
                                    MRP ₹{suggestion.price.toFixed(2)}
                                  </p>
                                )}
                              </div>
                            )}

                            <span
                              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${getMatchTypeColor(
                                suggestion.matchType
                              )} font-jakarta`}
                            >
                              {getMatchTypeIcon(suggestion.matchType)}
                              <span className="ml-1.5 capitalize">
                                {suggestion.matchType}
                              </span>
                            </span>
                          </div>
                        </div>

                        {((suggestion.categories &&
                          suggestion.categories.length > 0) ||
                          (suggestion.tags && suggestion.tags.length > 0)) && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {(suggestion.categories || [])
                              .slice(0, 2)
                              .map((category, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs bg-green-100 text-green-800 border border-green-200 font-medium font-jakarta"
                                >
                                  <Tag className="h-3 w-3 mr-1.5" />
                                  {category}
                                </span>
                              ))}
                            {(suggestion.tags || [])
                              .slice(0, 2)
                              .map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs bg-orange-100 text-orange-800 border border-orange-200 font-medium font-jakarta"
                                >
                                  <Hash className="h-3 w-3 mr-1.5" />
                                  {tag}
                                </span>
                              ))}
                          </div>
                        )}
                      </div>

                      <div className="flex-shrink-0">
                        <div
                          className={`p-2 rounded-full transition-all duration-300 ${
                            index === selectedIndex
                              ? "bg-primary-100 text-primary-600"
                              : "text-gray-400"
                          }`}
                        >
                          <ArrowRight className="h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading &&
              !error &&
              suggestions.length === 0 &&
              localValue.length >= 2 && (
                <div className="py-8 text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-gray-200">
                    <Search className="w-12 h-12 text-gray-400" />
                  </div>
                  <p className="text-lg font-semibold text-gray-700 font-jakarta">
                    No products found
                  </p>
                  <p className="text-sm text-gray-500 mt-2 font-jakarta">
                    Try different keywords or browse categories
                  </p>
                  <div className="mt-4 flex justify-center">
                    <button className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-all duration-300 font-medium font-jakarta transform hover:scale-105">
                      Browse Categories
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileSearchBar;
